import { useEffect, useRef, RefObject } from 'react';
import { addRemovableEventListener } from '../utils/domUtils';

type config = {
  interval?: number;
};

const defaultConfig = { interval: 10000 };

export default function useAutosave(onSave: () => void, refs: RefObject<HTMLElement>[] = [], config: config = {}) {
  const onSaveRef = useRef(onSave);
  const dirtyRef = useRef<RefObject<HTMLElement>>();
  const { interval } = Object.assign({}, defaultConfig, config);

  const save = () => onSaveRef.current();

  useEffect(() => {
    // Update the reference to the onSave function when it changes
    // otherwise state within that callback will be stale
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    const clearListeners = refs.map(ref =>
      addRemovableEventListener(ref.current, 'focus', () => {
        const intervalId = setInterval(() => {
          if (dirtyRef.current) {
            save();
            dirtyRef.current = undefined;
          }
        }, interval);

        const clearInputListener = addRemovableEventListener(ref.current, 'input', () => {
          dirtyRef.current = ref;
        });

        const clearBlurListener = addRemovableEventListener(ref.current, 'blur', () => {
          clearInterval(intervalId);
          clearInputListener();
          clearBlurListener();

          if (dirtyRef.current) {
            save();
            dirtyRef.current = undefined;
          }
        });
      })
    );

    return () => {
      clearListeners.forEach(clearListener => clearListener());
    };
  }, []);
}
